import type { TemplateSlot } from '../hooks/useTemplates';

const OPENING_VIDEO_SLOT_PATTERN = /^video([1-5])$/;

export const OPENING_FPS = 25;

const OPENING_START = 0;
const OPENING_END = 149;
const PRE_OUTLINE_END = 67;
const PRE_OUTLINE_TOTAL_FRAMES = PRE_OUTLINE_END - OPENING_START + 1;

export interface OpeningSegment {
  slotId: string;
  startFrame: number;
  endFrame: number;
  durationFrames: number;
  durationSeconds: number;
  required: true;
  visible: true;
}

export function getOpeningSegments(count: number): OpeningSegment[] {
  const safeCount = Math.min(5, Math.max(1, Math.floor(count || 1)));
  const base = Math.floor(PRE_OUTLINE_TOTAL_FRAMES / safeCount);
  const remainder = PRE_OUTLINE_TOTAL_FRAMES % safeCount;

  let cursor = OPENING_START;

  return Array.from({ length: safeCount }, (_, i) => {
    const duration = base + (i < remainder ? 1 : 0);
    const start = cursor;
    const preOutlineEnd = cursor + duration - 1;
    cursor += duration;

    const isLast = i === safeCount - 1;
    const end = isLast ? OPENING_END : preOutlineEnd;

    return {
      slotId: `video${i + 1}`,
      startFrame: start,
      endFrame: end,
      durationFrames: end - start + 1,
      durationSeconds: (end - start + 1) / OPENING_FPS,
      required: true as const,
      visible: true as const,
    };
  });
}

export function isOpeningVideoSlot(slotId: string): boolean {
  return OPENING_VIDEO_SLOT_PATTERN.test(slotId);
}

export function getOpeningVideoIndex(slotId: string): number | null {
  const match = slotId.match(OPENING_VIDEO_SLOT_PATTERN);
  return match ? Number(match[1]) : null;
}

export function getOpeningVideoCount(value: unknown): number {
  const parsed = Number(value ?? 1);
  if (!Number.isFinite(parsed)) return 1;
  return Math.min(5, Math.max(1, Math.trunc(parsed)));
}

export function isSlotVisible(slot: Pick<TemplateSlot, 'slotId' | 'visible'>, openingVideoCount: number): boolean {
  if (slot.visible === false) return false;

  const openingVideoIndex = getOpeningVideoIndex(slot.slotId);
  if (openingVideoIndex !== null) {
    return openingVideoIndex <= openingVideoCount;
  }

  return true;
}

export function isSlotRequired(slot: Pick<TemplateSlot, 'slotId' | 'required'>, openingVideoCount: number): boolean {
  const openingVideoIndex = getOpeningVideoIndex(slot.slotId);
  if (openingVideoIndex !== null) {
    return openingVideoIndex <= openingVideoCount;
  }

  return Boolean(slot.required);
}
